import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  Button,
  ButtonTypeEnum,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  Icon,
} from "@tenant/ui";
import clsx from "clsx";
import styles from "./GridV2PricingComparisonDialog.module.scss";
import {
  GridV2FaqAndTerms,
  GridV2FaqAndTermsDialog,
  useGridV2FaqAndTermsDialog,
} from "@/components/shared/GridV2FaqAndTerms";
import { GridV2PricingComparisonTable } from "@/components/shared/GridV2PricingComparisonTable";

export interface GridV2PricingComparisonDialogProps {
  open: boolean;
  /** X, Continue, Escape and overlay click: the client dismisses the modal. */
  onClose: () => void;
  /** Back arrow: the client returns to the modal that opened this one. */
  onBack: () => void;
  className?: string;
}

/**
 * Grid V2 A/B test: "Here's how new fixed pricing is different", the original
 * vs new fixed pricing comparison. Opened from the flight shop banner and the
 * Ways to pay modal once the grid-version gating is in place; standalone until
 * then. Below the desktop breakpoint the FAQ / Terms rows open a sheet over
 * this modal, which hides until the sheet closes.
 */
export const GridV2PricingComparisonDialog = ({
  open,
  onClose,
  onBack,
  className,
}: GridV2PricingComparisonDialogProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "GridV2PricingComparisonDialog");
  const { isDesktopAndUp } = useDeviceType();
  const faqAndTerms = useGridV2FaqAndTermsDialog(open);

  // The core DialogHeader renders its back button as a Radix Close, so back and
  // X would both dismiss. This header keeps "back to the opener" distinct.
  const header = (
    <div className={cn("header")}>
      <button
        type="button"
        className={cn("header-button")}
        aria-label={t("back")}
        onClick={onBack}
      >
        <Icon iconName={IconNameEnum.leftNavChevron} />
      </button>
      {/* Radix DialogClose closes through Dialog's onOpenChange, which calls onClose. */}
      <DialogClose asChild>
        <button
          type="button"
          className={cn("header-button")}
          aria-label={t("close.button")}
        >
          <Icon iconName={IconNameEnum.close} />
        </button>
      </DialogClose>
    </div>
  );

  const actions = (
    <div className={cn("actions")}>
      <Button
        buttonType={ButtonTypeEnum.Primary}
        className={cn("continue")}
        onClick={onClose}
      >
        {t("gridV2.pricingComparisonDialog.continue")}
      </Button>
    </div>
  );

  return (
    <>
      <Dialog open={open && !faqAndTerms.isOpen} onClose={onClose}>
        <DialogContent
          className={clsx(block, className)}
          header={header}
          bottomFixedContent={isDesktopAndUp ? undefined : actions}
        >
          <DialogTitle className={cn("title")}>
            {t("gridV2.pricingComparisonDialog.title")}
          </DialogTitle>
          <GridV2PricingComparisonTable className={cn("table")} />
          {isDesktopAndUp && actions}
          <GridV2FaqAndTerms
            className={cn("faq-and-terms")}
            onOpen={faqAndTerms.open}
          />
        </DialogContent>
      </Dialog>
      <GridV2FaqAndTermsDialog
        topic={faqAndTerms.topic}
        onClose={faqAndTerms.close}
      />
    </>
  );
};
